import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Download, Search, ChevronDown, X } from 'lucide-react';
import { ReportHeader } from '../../components/ReportHeader';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Multi-select dropdown ──────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val];
    onChange(next);
  };
  const clearAll = (e) => { e.stopPropagation(); onChange([]); };

  const displayLabel = selected.length === 0
    ? 'All'
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          height: 32, minWidth: 130, maxWidth: 200, padding: '0 28px 0 10px',
          fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6,
          background: selected.length ? '#f0f9ff' : '#fff',
          color: selected.length ? '#0369a1' : '#374151',
          cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis', position: 'relative',
        }}
      >
        {displayLabel}
        {selected.length > 0
          ? <X size={12} onClick={clearAll} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
          : <ChevronDown size={12} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 180, maxHeight: 260,
          overflowY: 'auto', padding: '4px 0', marginTop: 2,
        }}>
          {options.map(opt => (
            <label key={opt} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
              cursor: 'pointer', fontSize: 13, color: '#374151',
              background: selected.includes(opt) ? '#f0f9ff' : 'transparent',
            }}
              onMouseEnter={e => { if (!selected.includes(opt)) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = selected.includes(opt) ? '#f0f9ff' : 'transparent'; }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                style={{ accentColor: '#194449', cursor: 'pointer' }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────
const fmtDate = d => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

function CompletionRateBadge({ rate }) {
  if (rate == null) return <span>—</span>;
  const pct = parseFloat(rate);
  const bg    = pct >= 75 ? '#dcfce7' : pct >= 40 ? '#fef3c7' : '#fee2e2';
  const color = pct >= 75 ? '#166534' : pct >= 40 ? '#92400e' : '#991b1b';
  return <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700, background: bg, color }}>{pct.toFixed(1)}%</span>;
}

function StatusBadge({ active = true }) {
  return active
    ? <span style={{ background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Active</span>
    : <span style={{ background: '#f1f5f9', color: '#64748b', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>Retired</span>;
}

function CourseBadge({ type }) {
  if (!type) return <span>—</span>;
  return <span className="badge badge-blue">{type}</span>;
}

function Pagination({ page, totalPages, total, pageSize, onPage, testId = 'pagination' }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2), end = Math.min(totalPages, page + 2);
  const nums = [];
  for (let i = start; i <= end; i++) nums.push(i);
  return (
    <div className="pagination" data-testid={testId}>
      <span className="pagination-info">
        {((page - 1) * pageSize + 1).toLocaleString()}–{Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {nums.map(n => <button key={n} className={`page-btn${n === page ? ' active' : ''}`} onClick={() => onPage(n)}>{n}</button>)}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

function LoadingState() {
  return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
}

function EmptyState({ msg = 'No records found', hint = 'Try adjusting your filters' }) {
  return <div className="empty-state"><h3>{msg}</h3><p>{hint}</p></div>;
}

// ── Tab 1: Course List ─────────────────────────────────────────────────────────
function CourseListTab({ period, filterOpts, onFiltersApplied }) {
  const navigate  = useNavigate();
  const [filters, setFilters]   = useState({ solution_area: [], program_name: '', target_audience: [], source: [], search: '' });
  const [applied, setApplied]   = useState({ solution_area: [], program_name: '', target_audience: [], source: [], search: '' });
  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [exporting, setExporting] = useState(false);
  const PAGE_SIZE = 25;

  // Join array filters to comma-separated strings for API
  const buildParams = (f, p) => {
    const q = { page: p, page_size: PAGE_SIZE };
    if (f.solution_area?.length)   q.solution_area   = f.solution_area.join(',');
    if (f.program_name)            q.program_name    = f.program_name;
    if (f.target_audience?.length) q.target_audience = f.target_audience.join(',');
    if (f.source?.length)          q.source          = f.source.join(',');
    if (f.search)                  q.search          = f.search;
    if (period.from) { q.enrolled_from = period.from; q.from_date = period.from; }
    if (period.to)   { q.enrolled_to   = period.to;   q.to_date   = period.to; }
    return q;
  };

  const fetchList = useCallback(async (p, f) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/course-activity/list`,
        { params: buildParams(f, p), withCredentials: true });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchList(1, applied); }, [period.from, period.to]); // eslint-disable-line

  const handleApply = () => {
    setApplied({ ...filters });
    fetchList(1, filters);
    // Notify parent to update KPI tiles with these filters + period
    if (onFiltersApplied) onFiltersApplied(buildParams(filters, 1));
  };
  const handleReset = () => {
    const blank = { solution_area: [], program_name: '', target_audience: [], source: [], search: '' };
    setFilters(blank); setApplied(blank); fetchList(1, blank);
    if (onFiltersApplied) onFiltersApplied({});
  };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/api/reports/course-activity/list`,
        { params: { ...buildParams(applied, 1), page_size: 5000 }, withCredentials: true });
      const rows = r.data.data || [];
      const headers = ['Course Name', 'MS Course ID', 'ESI Course Code', 'Solution Area', 'Course Type',
        'Status', 'Duration', 'Source (Multilingual Audio)', 'Project Name', 'Enrollments', 'Completions', 'Completion Rate (%)'];
      const csv = [headers.join(','), ...rows.map(row => [
        `"${(row.name || '').replace(/"/g, '""')}"`,
        row.ms_course_id || '',
        row.esi_course_code || '',
        `"${(row.solution_area || '').replace(/"/g, '""')}"`,
        row.course_type || '',
        'Active',
        row.course_duration || '',
        row.multilingual_audio || '',
        `"${(row.project_name || '').replace(/"/g, '""')}"`,
        row.enrolled_learners || 0,
        row.trained_learners || 0,
        row.completion_rate != null ? row.completion_rate : '',
      ].join(','))].join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = 'course_list.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  const LINK_STYLE = { color: '#194449', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 };

  return (
    <div>
      {/* Filters */}
      <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="filter-group">
          <label className="filter-label">Solution Area</label>
          <MultiSelect
            options={filterOpts.domains || []}
            selected={filters.solution_area}
            onChange={v => setF('solution_area', v)}
            testId="ca-filter-domain"
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">Project</label>
          <select className="filter-select" value={filters.program_name} onChange={e => setF('program_name', e.target.value)} data-testid="ca-filter-project">
            <option value="">All</option>
            {(filterOpts.projects || []).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Course Type</label>
          <MultiSelect
            options={filterOpts.course_types || []}
            selected={filters.target_audience}
            onChange={v => setF('target_audience', v)}
            testId="ca-filter-type"
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">Source</label>
          <MultiSelect
            options={filterOpts.sources || []}
            selected={filters.source}
            onChange={v => setF('source', v)}
            testId="ca-filter-source"
          />
        </div>
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="Course name…" value={filters.search}
            onChange={e => setF('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleApply()}
            data-testid="ca-filter-search" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="ca-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset} data-testid="ca-reset-btn">Reset</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">Course List</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} courses)</span>
          </div>
          <button onClick={handleExport} disabled={exporting} data-testid="ca-export-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <Download size={16} /> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>
        {loading ? <LoadingState /> : data.length === 0 ? <EmptyState msg="No courses found" /> : (
          <>
            <div className="table-scroll">
              <table data-testid="ca-table">
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>MS Course ID</th>
                    <th>ESI Code</th>
                    <th>Solution Area</th>
                    <th>Course Type</th>
                    <th>Status</th>
                    <th>Published Date</th>
                    <th>Retired Date</th>
                    <th className="td-right">Enrollments</th>
                    <th className="td-right">Completions</th>
                    <th className="td-right">Badges</th>
                    <th>Duration</th>
                    <th>Source</th>
                    <th>Project</th>
                    <th className="td-right">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.class_id || i} data-testid={`ca-row-${i}`}>
                      <td style={{ maxWidth: 260 }} title={row.name}>{row.name || '—'}</td>
                      <td>{row.ms_course_id || 'N/A'}</td>
                      <td>{row.esi_course_code || 'N/A'}</td>
                      <td>{row.solution_area || '—'}</td>
                      <td><CourseBadge type={row.course_type} /></td>
                      <td><StatusBadge active={!row.archived} /></td>
                      <td>{fmtDate(row.lms_published_at)}</td>
                      <td>{fmtDate(row.deactivated_at || row.archived_at)}</td>
                      <td className="td-right" style={{ fontWeight: 600 }}>
                        <span data-testid={`ca-enrolled-link-${i}`} style={LINK_STYLE}
                          onClick={() => navigate(`/reports/course-activity/${encodeURIComponent(row.class_id)}/learners?view=enrolled&course=${encodeURIComponent(row.name || row.class_id)}`)}>
                          {(row.enrolled_learners || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="td-right" style={{ fontWeight: 600 }}>
                        <span data-testid={`ca-trained-link-${i}`} style={LINK_STYLE}
                          onClick={() => navigate(`/reports/course-activity/${encodeURIComponent(row.class_id)}/learners?view=completed&course=${encodeURIComponent(row.name || row.class_id)}`)}>
                          {(row.trained_learners || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="td-right">{row.badge_count != null ? row.badge_count : '—'}</td>
                      <td>{row.course_duration || '—'}</td>
                      <td>{row.multilingual_audio || '—'}</td>
                      <td>{row.project_name || '—'}</td>
                      <td className="td-right"><CompletionRateBadge rate={row.completion_rate} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
              onPage={p => fetchList(p, applied)} testId="ca-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab 2: All Enrollments ─────────────────────────────────────────────────────
function EnrollmentsTab({ period }) {
  const [filters, setFilters]   = useState({ search: '', partner_name: '', solution_area: '', learner_status: '' });
  const [applied, setApplied]   = useState({ search: '', partner_name: '', solution_area: '', learner_status: '' });
  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [filterOpts, setFilterOpts] = useState({ partners: [], solution_areas: [] });
  const PAGE_SIZE = 25;
  const firstLoad = useRef(false);

  useEffect(() => {
    if (!firstLoad.current) {
      firstLoad.current = true;
      axios.get(`${API}/api/reports/filters`, { withCredentials: true })
        .then(r => setFilterOpts(r.data)).catch(console.error);
    }
    fetchList(1, applied);
  }, [period.from, period.to]); // eslint-disable-line

  const buildParams = (f, p) => {
    const q = { page: p, page_size: PAGE_SIZE };
    if (f.search)         q.search         = f.search;
    if (f.partner_name)   q.partner_name   = f.partner_name;
    if (f.learner_status) q.learner_status  = f.learner_status;
    if (period.from)      q.from_date      = period.from;
    if (period.to)        q.to_date        = period.to;
    return q;
  };

  const fetchList = async (p, f) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/allup/list`,
        { params: buildParams(f, p), withCredentials: true });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApply = () => { setApplied({ ...filters }); fetchList(1, filters); };
  const handleReset = () => {
    const blank = { search: '', partner_name: '', solution_area: '', learner_status: '' };
    setFilters(blank); setApplied(blank); fetchList(1, blank);
  };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const handleExport = () => {
    const headers = ['Course Name', 'Solution Area', 'Enrolled Date', 'Partner', 'Domain', 'Learner Name', 'Learner Email', 'Status'];
    const csv = [headers.join(','), ...data.map(row => [
      `"${(row.course_name || '').replace(/"/g, '""')}"`,
      `"${(row.solution_area || '').replace(/"/g, '""')}"`,
      fmtDate(row.enrolled_at),
      `"${(row.partner_name || '').replace(/"/g, '""')}"`,
      row.domain || '',
      `"${[row.first_name, row.last_name].filter(Boolean).join(' ').replace(/"/g, '""')}"`,
      row.email || '',
      row.status || '',
    ].join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'all_enrollments.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="filter-group">
          <label className="filter-label">Search Course / Learner</label>
          <div style={{ position: 'relative' }}>
            <Search size={12} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <input type="text" className="filter-input" placeholder="Name, email, course…" value={filters.search}
              onChange={e => setF('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleApply()}
              style={{ paddingLeft: 26 }} data-testid="enrol-search" />
          </div>
        </div>
        <div className="filter-group">
          <label className="filter-label">Partner</label>
          <select className="filter-select" value={filters.partner_name} onChange={e => setF('partner_name', e.target.value)} data-testid="enrol-partner">
            <option value="">All Partners</option>
            {(filterOpts.partners || []).slice(0, 200).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select className="filter-select" value={filters.learner_status} onChange={e => setF('learner_status', e.target.value)} data-testid="enrol-status">
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="enrolled">In Progress</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="enrol-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset}>Reset</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">All Enrollments</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} records)</span>
          </div>
          <button onClick={handleExport} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <Download size={16} /> CSV
          </button>
        </div>
        {loading ? <LoadingState /> : data.length === 0 ? <EmptyState /> : (
          <>
            <div className="table-scroll">
              <table data-testid="enrol-table">
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>Solution Area</th>
                    <th>Enrolled Date</th>
                    <th>Partner</th>
                    <th>Domain</th>
                    <th>Learner Name</th>
                    <th>Learner Email</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} data-testid={`enrol-row-${i}`}>
                      <td style={{ maxWidth: 240 }} title={row.course_name}>{row.course_name || '—'}</td>
                      <td>{row.solution_area || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.enrolled_at)}</td>
                      <td>{row.partner_name || '—'}</td>
                      <td>{row.domain || '—'}</td>
                      <td>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.email}>{row.email || '—'}</td>
                      <td>
                        {row.status === 'Completed'
                          ? <span className="badge badge-green">{row.status}</span>
                          : <span className="badge badge-blue">{row.status || 'Enrolled'}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
              onPage={p => fetchList(p, applied)} testId="enrol-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab 3: All Completions ─────────────────────────────────────────────────────
function CompletionsTab({ period }) {
  const [filters, setFilters]   = useState({ search: '', partner_name: '', solution_area: '', from_date: '', to_date: '' });
  const [applied, setApplied]   = useState({ search: '', partner_name: '', solution_area: '', from_date: '', to_date: '' });
  const [data, setData]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [filterOpts, setFilterOpts] = useState({ partners: [], solution_areas: [] });
  const PAGE_SIZE = 25;
  const firstLoad = useRef(false);

  useEffect(() => {
    if (!firstLoad.current) {
      firstLoad.current = true;
      axios.get(`${API}/api/reports/filters`, { withCredentials: true })
        .then(r => setFilterOpts(r.data)).catch(console.error);
    }
    const initF = { ...filters, from_date: period.from || '', to_date: period.to || '' };
    setFilters(initF);
    setApplied(initF);
    fetchList(1, initF);
  }, [period.from, period.to]); // eslint-disable-line

  const buildParams = (f, p) => {
    const q = { page: p, page_size: PAGE_SIZE };
    if (f.search)         q.search         = f.search;
    if (f.partner_name)   q.partner_name   = f.partner_name;
    if (f.solution_area)  q.solution_area  = f.solution_area;
    if (f.from_date)      q.from_date      = f.from_date;
    if (f.to_date)        q.to_date        = f.to_date;
    return q;
  };

  const fetchList = async (p, f) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/reports/completion/list`,
        { params: buildParams(f, p), withCredentials: true });
      setData(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleApply = () => { setApplied({ ...filters }); fetchList(1, filters); };
  const handleReset = () => {
    const blank = { search: '', partner_name: '', solution_area: '', from_date: '', to_date: '' };
    setFilters(blank); setApplied(blank); fetchList(1, blank);
  };
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const handleExport = () => {
    const headers = ['Course Name', 'Solution Area', 'Learner Name', 'Learner Email', 'Partner', 'Domain', 'Enrolled Date', 'Completed Date', 'Duration'];
    const csv = [headers.join(','), ...data.map(row => [
      `"${(row.class_name || '').replace(/"/g, '""')}"`,
      `"${(row.solution_area || '').replace(/"/g, '""')}"`,
      `"${(row.student_name || '').replace(/"/g, '""')}"`,
      row.email || '',
      `"${(row.partner_name || '').replace(/"/g, '""')}"`,
      row.domain || '',
      fmtDate(row.enrolled_at),
      fmtDate(row.completed_at),
      row.course_duration || '',
    ].join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'all_completions.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div className="filter-group">
          <label className="filter-label">Search Course / Learner</label>
          <input type="text" className="filter-input" placeholder="Name, email, course…" value={filters.search}
            onChange={e => setF('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && handleApply()} data-testid="comp-search" />
        </div>
        <div className="filter-group">
          <label className="filter-label">Partner</label>
          <select className="filter-select" value={filters.partner_name} onChange={e => setF('partner_name', e.target.value)} data-testid="comp-partner">
            <option value="">All Partners</option>
            {(filterOpts.partners || []).slice(0, 200).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">Solution Area</label>
          <select className="filter-select" value={filters.solution_area} onChange={e => setF('solution_area', e.target.value)} data-testid="comp-solution-area">
            <option value="">All</option>
            {(filterOpts.solution_areas || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filter-group">
          <label className="filter-label">From</label>
          <input type="date" className="filter-input" value={filters.from_date}
            onChange={e => setF('from_date', e.target.value)} data-testid="comp-from" />
        </div>
        <div className="filter-group">
          <label className="filter-label">To</label>
          <input type="date" className="filter-input" value={filters.to_date}
            onChange={e => setF('to_date', e.target.value)} data-testid="comp-to" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <button className="btn btn-primary" onClick={handleApply} data-testid="comp-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={handleReset}>Reset</button>
        </div>
      </div>

      <div className="table-container">
        <div className="table-header">
          <div>
            <span className="table-title">All Completions</span>
            <span className="table-count" style={{ marginLeft: 8 }}>({total.toLocaleString()} records)</span>
          </div>
          <button onClick={handleExport} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#217346', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <Download size={16} /> CSV
          </button>
        </div>
        {loading ? <LoadingState /> : data.length === 0 ? <EmptyState /> : (
          <>
            <div className="table-scroll">
              <table data-testid="comp-table">
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>Solution Area</th>
                    <th>Learner Name</th>
                    <th>Learner Email</th>
                    <th>Partner</th>
                    <th>Domain</th>
                    <th>Enrolled Date</th>
                    <th>Completed Date</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.id || i} data-testid={`comp-row-${i}`}>
                      <td style={{ maxWidth: 240 }} title={row.class_name}>{row.class_name || '—'}</td>
                      <td>{row.solution_area || '—'}</td>
                      <td>{row.student_name || '—'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.email}>{row.email || '—'}</td>
                      <td>{row.partner_name || '—'}</td>
                      <td>{row.domain || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.enrolled_at)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.completed_at)}</td>
                      <td>{row.course_duration || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
              onPage={p => fetchList(p, applied)} testId="comp-pagination" />
          </>
        )}
      </div>
    </div>
  );
}

// ── Tab 4: All Assessments ─────────────────────────────────────────────────────
function AssessmentsTab() {
  return (
    <div className="table-container" data-testid="assessments-tab">
      <div style={{ padding: '60px 40px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Assessment Data Coming Soon</h3>
        <p style={{ color: '#6b7280', fontSize: 13, maxWidth: 400, margin: '0 auto' }}>
          Assessment data will be available after LMS sync is configured. Scores, pass rates, and attempt history will appear here once the integration is enabled.
        </p>
      </div>
    </div>
  );
}

// ── Tab 5: All Badges Awarded ──────────────────────────────────────────────────
function BadgesTab() {
  return (
    <div className="table-container" data-testid="badges-tab" style={{ padding: '40px 32px', textAlign: 'center' }}>
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px', fontSize: 24,
        }}>🏅</div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 8 }}>
          Badges Data Coming Soon
        </h3>
        <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: 0 }}>
          Badge award data will be available once the Credly integration is fully configured.
          Per-learner badge records and award history will appear here when the integration is enabled.
        </p>
      </div>
    </div>
  );
}

// ── Tab navigation styles ──────────────────────────────────────────────────────
const TABS = [
  { id: 'course-list',    label: 'Course List' },
  { id: 'enrollments',   label: 'All Enrollments' },
  { id: 'completions',   label: 'All Completions' },
  { id: 'assessments',   label: 'All Assessments' },
  { id: 'badges',        label: 'All Badges Awarded' },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function CourseActivityReport() {
  const [period, setPeriod]       = useState({ from: '', to: '' });
  const [activeTab, setActiveTab] = useState('course-list');
  const [loadedTabs, setLoadedTabs] = useState({ 'course-list': true });
  const [stats, setStats]         = useState({ total_courses: 0, total_enrollments: 0, total_completions: 0 });
  const [filterOpts, setFilterOpts] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/reports/course-activity/filters`, { withCredentials: true })
      .then(r => setFilterOpts(r.data)).catch(console.error);
    fetchStats({});
  }, []); // eslint-disable-line

  const fetchStats = async (f) => {
    try {
      const r = await axios.get(`${API}/api/reports/course-activity/stats`,
        { params: f, withCredentials: true });
      setStats(r.data);
    } catch (e) { console.error(e); }
  };

  const handlePeriodChange = useCallback(({ startDate, endDate }) => {
    const from = startDate || '', to = endDate || '';
    setPeriod({ from, to });
    fetchStats({ from_date: from, to_date: to });
  }, []); // eslint-disable-line

  // Called by CourseListTab when filters are applied — refreshes KPI tiles
  const handleFiltersApplied = useCallback((filterParams) => {
    fetchStats(filterParams);
  }, []); // eslint-disable-line

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    setLoadedTabs(prev => ({ ...prev, [tab]: true }));
  };

  return (
    <div data-testid="course-activity-page">
      <ReportHeader
        reportTitle="Course Activity Report"
        primaryTable="courses"
        onChange={handlePeriodChange}
      />
      <div className="page-header">
        <h1>Course Activity</h1>
      </div>

      {/* KPI Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        <div className="stat-card" data-testid="ca-stat-courses">
          <div className="stat-value green">{stats.total_courses.toLocaleString()}</div>
          <div className="stat-label">Total Courses</div>
        </div>
        <div className="stat-card" data-testid="ca-stat-enrollments">
          <div className="stat-value green">{stats.total_enrollments.toLocaleString()}</div>
          <div className="stat-label">Total Enrollments</div>
        </div>
        <div className="stat-card" data-testid="ca-stat-completions">
          <div className="stat-value green">{stats.total_completions.toLocaleString()}</div>
          <div className="stat-label">Total Completions</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0',
        marginBottom: 20, overflowX: 'auto',
      }} data-testid="ca-tab-nav" role="tablist">
        {TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            data-testid={`ca-tab-${tab.id}`}
            onClick={() => handleTabSwitch(tab.id)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              fontFamily: '"DM Sans", sans-serif',
              color: activeTab === tab.id ? '#194449' : '#6b7280',
              borderBottom: activeTab === tab.id ? '2px solid #194449' : '2px solid transparent',
              marginBottom: -2,
              transition: 'color 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content — lazy rendered */}
      <div style={{ display: activeTab === 'course-list' ? 'block' : 'none' }}>
        {loadedTabs['course-list'] && <CourseListTab period={period} filterOpts={filterOpts} onFiltersApplied={handleFiltersApplied} />}
      </div>
      <div style={{ display: activeTab === 'enrollments' ? 'block' : 'none' }}>
        {loadedTabs['enrollments'] && <EnrollmentsTab period={period} />}
      </div>
      <div style={{ display: activeTab === 'completions' ? 'block' : 'none' }}>
        {loadedTabs['completions'] && <CompletionsTab period={period} />}
      </div>
      <div style={{ display: activeTab === 'assessments' ? 'block' : 'none' }}>
        {loadedTabs['assessments'] && <AssessmentsTab />}
      </div>
      <div style={{ display: activeTab === 'badges' ? 'block' : 'none' }}>
        {loadedTabs['badges'] && <BadgesTab period={period} />}
      </div>
    </div>
  );
}
